import React, { ReactElement, useState, useEffect } from 'react';
import { getLabelFallback, convertNumber, handleHTTPError, scrollUpPage, isThereAnAddedWarranty, getDiscountNames } from '@utils/utils';
import {  deleteOrderItem, postPromoCode, getEspot } from '@utils/services';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '@redux-actions/';

import PageTitle from '@components/PageTitle';
import CartProductTile from '@components/CartProductTile';
import Insurance from '@components/Insurance';
import TextBanner from '@components/TextBanner';
import Close from '@images/svg-icons/close.svg';

import CardProductTileSkeleton from '@components/skeletons/CardProductTileSkeleton';

import SimplePlusIcon from '@images/svg-icons/simple-plus.svg';
import SimpleMinusIcon from '@images/svg-icons/simple-minus.svg';
import SimpleElementSkeleton from './skeletons/SimpleElementSkeleton';

import FunctionalError from '@images/svg-icons/functionalError.svg';
import FunctionalWarning from '@images/svg-icons/functionalWarning.svg';

type CartProps = {
    checkoutClick: () => void;
    smartGlassesError: string;
    children: any;
}

const Cart = ({ checkoutClick, smartGlassesError, children } : CartProps): ReactElement => {
    const methods = useForm<any>();

    const dispatch = useDispatch();
    
    const formattedOrderItems = useSelector(state => state.cart.formattedOrderItems);
    const cart = useSelector(state => state.cart.cart);
    const isLoading = useSelector(state => state.cart.isLoading);
    const config = useSelector(state => state.cart.config);
    const showPromoCodeFeedback = useSelector(state => state.cart.showPromoCodeFeedback);
    const insuranceError = useSelector(state => state.cart.insuranceError);
    const [visiblePromo, setPromoVisible] = useState(false);
    const [visibleVerify, setVerifyVisible] = useState(false);
    const [promoCodeFeedback, setPromoCodeFeedback] = useState(<></>);
    const [espotEarlyAccessTooltip,setEspotEarlyAccessTooltip] = useState(null);

    const speedyOrderDetails = useSelector(state => state.cart.speedyOrderDetails);
    const [isOrderSpeedyErrorActive, setIsOrderSpeedyErrorActive] = useState(false);
    const [isOrderSpeedyWarningActive, setIsOrderSpeedyWarningActive] = useState(false);
    const isMobile = useSelector(state => state.cart.isMobile);
    

    const onChangeHandler = () =>{
        if($('#promo-code').val()!=""){
            $('.cart-accordion-form-feedback .error').text("");
        }
    }

    const onResetHandler = () =>{
        $('.cart-accordion-form-feedback .error').text("");
    }

    const submitPromoCode = async (data) => {

        dispatch(actions.startLoading());
        await postPromoCode(
            config.hostname,
            {
                "promoCode": data['promo-code']
            }
        ).then(() => {
            dispatch(actions.getCart(config.mockRequests, config.hostname));
            dispatch(actions.setShowPromoCodeFeedback(true));

            window?.tealium_data2track?.push({
                'tealium_event': 'OrderDiscountCode-Applied',
                'id': 'Event',
                "Events_DiscountApplied": "1",
                'Order_DiscountCode': data['promo-code'],
                "Order_DiscountName": getDiscountNames(cart.adjustment),
                'Tracking_Type': ''
            });
            
            setPromoCodeFeedback(
                <span className="success">
                    {getLabelFallback('PROMO_SUCCESS')}
                </span>
            );
            methods.reset();

            setPromoVisible(false);

        }).catch((err) => {
            var errorMsg=err.response.data.errors[0].errorMessage;
            if(errorMsg.includes("PROMO_CODE_RIA_MIN")){
                errorMsg=errorMsg.split("PROMO_CODE_RIA_MIN: ")[1]
            }
            if(!!err && !!err.response) {
                console.error('>>> Submit promo code => error status:', err.response.status, ', error: ', err);

                if (errorMsg != null && (errorMsg.includes("is not compatible with insurance") || !errorMsg.includes("PROMO_CODE_RIA_MIN"))){
                    switch (err.response.status){
                    case 400:
                        window?.tealium_data2track?.push({
                        'tealium_event': 'OrderDiscountCode-Tentative',
                        'id': 'Event',
                        'Events_DiscountTentative' : '1',
                        'Order_DiscountCode': data['promo-code'],
                        'Order_DiscountName': '',
                        'Error_Code': err.response.status.toString(),
                        'Error_Message': errorMsg,
                        'Error_Source': 'Server'
                    });
                     break;
                    case 401:
                    case 403: dispatch(actions.setShowSessionModal(true));
                            window?.tealium_data2track?.push({
                                'tealium_event': 'OrderDiscountCode-Tentative',
                                'id': 'Event',
                                'Events_DiscountTentative' : '1',
                                'Order_DiscountCode': data['promo-code'],
                                'Order_DiscountName': '',
                                'Error_Code': err.response.status.toString(),
                                'Error_Message': errorMsg,
                                'Error_Source': 'Server'
                            });
                        break;
                    case 404: 
                        window?.tealium_data2track?.push({
                            'tealium_event': 'OrderDiscountCode-Tentative',
                            'id': 'Event',
                            'Events_DiscountTentative' : '1',
                            'Order_DiscountCode': data['promo-code'],
                            'Order_DiscountName': '',
                            'Error_Code': err.response.status.toString(),
                            'Error_Message': errorMsg,
                            'Error_Source': 'User'
                        });
                        break;
                    default: dispatch(actions.setGenericErrorModal(true, getLabelFallback('GENERIC_ERROR_MODAL_TEXT_LINE')));
                        window?.tealium_data2track?.push({
                            'tealium_event': 'OrderDiscountCode-Tentative',
                            'id': 'Event',
                            'Events_DiscountTentative' : '1',
                            'Order_DiscountCode': data['promo-code'],
                            'Order_DiscountName': '',
                            'Error_Code': err.response.status.toString(),
                            'Error_Message': errorMsg,
                            'Error_Source': 'Server'
                        });
                } 
                }else{
                    switch (err.response.status){
                        case 400:
                            window?.tealium_data2track?.push({
                            'tealium_event': 'Error',
                            'id': 'Event',
                            Events_Error: '1',
                            'Order_DiscountCode': data['promo-code'],
                            'Error_Code': '',
                            'Error_Message': errorMsg,
                            'Error_Details': errorMsg,
                            'Error_Source': 'Server',
                            'Tracking_Type': ''
                        });
                         break;
                        case 401:
                        case 403: dispatch(actions.setShowSessionModal(true));
                                window?.tealium_data2track?.push({
                                    'tealium_event': 'Error',
                                    'id': 'Event',
                                    Events_Error: '1',
                                    'Order_DiscountCode': data['promo-code'],
                                    'Error_Code': '',
                                    'Error_Message': errorMsg,
                                    'Error_Details': errorMsg,
                                    'Error_Source': 'Server',
                                    'Tracking_Type': ''
                                });
                            break;
                        case 404: 
                            window?.tealium_data2track?.push({
                                'tealium_event': 'Error',
                                'id': 'Event',
                                Events_Error: '1',
                                'Order_DiscountCode': data['promo-code'],
                                'Error_Code': '',
                                'Error_Message': errorMsg,
                                'Error_Details': errorMsg,
                                'Error_Source': 'User',
                                'Tracking_Type': ''
                            });
                            break;
                        default: dispatch(actions.setGenericErrorModal(true, getLabelFallback('GENERIC_ERROR_MODAL_TEXT_LINE')));
                            window?.tealium_data2track?.push({
                                'tealium_event': 'Error',
                                'id': 'Event',
                                Events_Error: '1',
                                'Order_DiscountCode': data['promo-code'],
                                'Error_Code': '',
                                'Error_Message': errorMsg,
                                'Error_Details': errorMsg,
                                'Error_Source': 'Server',
                                'Tracking_Type': ''
                            });
                    } 
                }
                
            }
           
            dispatch(actions.errorLoading());
            dispatch(actions.setShowPromoCodeFeedback(true));

            errorMsg=err.response.data.errors[0].errorMessage;

            if(errorMsg != null && errorMsg.includes("is not compatible with insurance")) {
                setPromoCodeFeedback(
                    <span className="error">
                        {getLabelFallback('PROMO_ERROR_INSURANCE').replace('{PROMO_CODE}', data['promo-code'])}
                    </span>
                );
            } else if(errorMsg != null && !errorMsg.includes("PROMO_CODE_RIA_MIN")) {
                setPromoCodeFeedback(
                    <span className="error">
                        {getLabelFallback('PROMO_ERROR').replace('{PROMO_CODE}', data['promo-code'])}
                    </span>
                );
            } else {
                setPromoCodeFeedback(
                    <span className="error">
                        {errorMsg.split("PROMO_CODE_RIA_MIN: ")[1]}
                    </span>
                );
            }
        })
    };

    const handleEditOrderItem = (frameId, lensesAddonsIds, orderItemId, prescription, laResults) => {
        /****** SCROLL UP ******/
        scrollUpPage(window);
        /***********************/
        //Controllare se siamo in spedizione speedy e se il prodotto è uno speedy
        var isSpeedyActive = false;
        const itemSpeedyActiveId = `item-${orderItemId}-speedy-active`;
        const itemSpeedyActive = document.getElementById(itemSpeedyActiveId);
        if(!!itemSpeedyActive) {
        	let isItemSpeedyActive = speedyOrderDetails?.glassesDetails.some(i => i.frameOrderItemId === orderItemId && i.speedyActive) ?? false;
        	let isItemSpeedyEligible = speedyOrderDetails?.glassesDetails.some(i => i.frameOrderItemId === orderItemId && i.speedyEligible) ?? false;
        	itemSpeedyActive.value = (!!isItemSpeedyActive && !!isItemSpeedyEligible).toString();
        	isSpeedyActive = itemSpeedyActive;
    	}

        const index = cart.orderItem.findIndex(item => item.orderItemId === orderItemId);
        const eppCatEntryId = isThereAnAddedWarranty(cart, orderItemId) ? cart.orderItem?.find(warrantyItem => (warrantyItem.comments == "WARRANTY" && warrantyItem.xitem_field1 == orderItemId))?.productId : null;
        config.callFromComponentToGl.editOrderItem(frameId, lensesAddonsIds, cart.orderId, orderItemId, index, prescription, eppCatEntryId, laResults, isSpeedyActive, config.speedyRxOfferPrice, config.speedyRxListPrice);
    }

    const handleRemoveOrderItem = (orderItemId) => {
        dispatch(actions.startLoading());
        
        deleteOrderItem(config.hostname ,
            {
                "storeId": config.storeId,
                "orderId": cart.orderId,
                "orderItemId": orderItemId, 
                "calculationUsage": "-1,-2,-4,-5,-6,-7",
                "forGiftItem": ""
            }
        ).then(() => {
            dispatch(actions.getCart(config.mockRequests, config.hostname));
            dispatch(actions.setSpeedyDetails(config.hostname, cart.orderId));
            dispatch(actions.setDisableNavigation(-1));
        }).catch(err => {
            console.error('>>> Remove order item => error status:', err.status, ', error: ', err);
            handleHTTPError(dispatch, actions, err);
        })
    }

    //get items in the cart
    const getCartItems = (orderItem) => {
        const hasInsurance = !!cart && !!cart.adjustment && cart.adjustment.find(adj => adj.code === 'INSADJ');

        const classes = !!hasInsurance ? 'insurance-cart-product-tile' : '';

        return orderItem.map((item, index) => {
            return <React.Fragment key={'cart-product-tile' + index}>
                        <CartProductTile 
                            item={item}
                            orderId={cart?.orderId}
                            editOrderItem={() => {handleEditOrderItem(item.frame.productId, item.lensesAddonsIds, item.frame.orderItemId, item.prescription, item.frame.xitem_laResults)}}
                            removeOrderItem={(value) => handleRemoveOrderItem(value)}
                            classes={classes}
                        />
                   </React.Fragment>;
        });
    };

    const getResumeLabel = (orderItem) => orderItem.length > 1 ? getLabelFallback('ITEM_SUMMARY_LABEL_PLURAL') : getLabelFallback('ITEM_SUMMARY_LABEL_SINGULAR');

    useEffect(() => {
        var idmeButton = document.getElementById('idme-wallet-button');
        if (idmeButton != null) {
            //id.me re-load script
            window.BindIDme();
            document.querySelector("[class*='idme-trigger-link idme-unify-button']")?.setAttribute('target', '_new');
        }
    });

    useEffect(() => {
        if(smartGlassesError === 'TOO_MANY_GLASSES' && document.getElementById('removeAllSmartGlasses') != null) {
            document.getElementById('removeAllSmartGlasses').addEventListener('click', () => {
                const noSmartGlasses = formattedOrderItems.filter(item => (!!item.moCo.prod_hierarchy_1 && item.moCo.prod_hierarchy_1.toLowerCase() !== 'electronics'));
                const noSmartGlassesFrameIds = noSmartGlasses.map(n => n.frame.orderItemId);
                handleRemoveOrderItem(noSmartGlassesFrameIds);
            });
        }       
    }, [smartGlassesError]);

    const [cartProductTile, setCartProductTile] = useState(null);

    
    useEffect(() => {
	     getEspot(config.hostname, 'X_Cart_EarlyAccessTooltip').then(res => {
	        if (res) {
	        setEspotEarlyAccessTooltip(res as string)
	        }
	      })
	      .catch(err => {
	        console.error('>>> Get Espot early access => error status:', err.status, ', error: ', err);
	        handleHTTPError(dispatch, actions, err);
	      });
  	}, [espotEarlyAccessTooltip]);
  	
    useEffect(() => {
        if(!!formattedOrderItems) {
            setCartProductTile(getCartItems(formattedOrderItems));
        }
    }, [formattedOrderItems]);

    useEffect(() => {
        if(config.isSpeedyRxEnabled === 'true' && !!speedyOrderDetails){
            if(speedyOrderDetails.orderSpeedyActive && (speedyOrderDetails.mixedCart || !speedyOrderDetails.orderSpeedyEligible)){
                setIsOrderSpeedyWarningActive(false);
                setIsOrderSpeedyErrorActive(true);

                window?.tealium_data2track.push({
                    'id': 'Event',
                    'Events_Error': '1',
                    'Error_Source': 'User',
                    'Error_Code': 'Speedy Mixed Cart',
                    'Error_Details': 'Mixed cart – Incompatible shipping methods',
                    'Error_Message': getLabelFallback('SPEEDY_FUNCTIONAL_ERROR_BODY'),
                });
            } else if(speedyOrderDetails.orderSpeedyActive && speedyOrderDetails.hasBeenAligned){
                setIsOrderSpeedyErrorActive(false);
                setIsOrderSpeedyWarningActive(true);
            } else {
                setIsOrderSpeedyErrorActive(false);
                setIsOrderSpeedyWarningActive(false);
            }
        }
    }, [speedyOrderDetails])

    // Add Mention Me referee script to end of <body> on mount and remove on unmount
    useEffect(() => {
        if (!config?.mentionMeEnabled) return;

        const scriptId = 'mention-me-referee-script';
        let appended = false;

        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.type = 'text/javascript';
            const locale = 'en_US';
            const situation = 'checkout';
            script.src =  config.mentionMeReferee + `?situation=${encodeURIComponent(situation)}&locale=${encodeURIComponent(locale)}`;
            script.async = true;
            document.body.appendChild(script);
            appended = true;
        }

        return () => {
            if (appended) {
                const s = document.getElementById(scriptId);
                if (s && s.parentNode) {
                    s.parentNode.removeChild(s);
                }
                if (window.MentionMeFiredTags != undefined && window.MentionMeFiredTags != '') {
                    var situation      = 'checkout';
                    var implementation = 'link';
                    if (window.MentionMeFiredTags[implementation + situation] == true) {
                        delete window.MentionMeFiredTags[implementation + situation];
                    }
                }
            }
        };
    }, []);

    return (
        <section className="cart">
            <div className="cart-title">
                <PageTitle label={ getLabelFallback('CART_PAGE_TITLE') } />
                {!!formattedOrderItems && 
                    <div className="cart-title-resume">
                        <span>{formattedOrderItems.length} { getResumeLabel(formattedOrderItems) } - <strong>{getLabelFallback('CURRENCY_LABEL')}{convertNumber(cart.grandTotal)}</strong></span>
                    </div>
                }
            </div>

            {/* SPEEDY RX WARNING */}
            {!!isMobile === false && (!!isOrderSpeedyErrorActive || !!isOrderSpeedyWarningActive) &&
                <div className={`cart-speedy-alerts ${!!isOrderSpeedyErrorActive ? 'speedy-error' : !!isOrderSpeedyWarningActive ? 'speedy-warning' : ''}`}>
                    <div className='cart-speedy-alerts-title'>
                        <div className='cart-speedy-alerts-title-icon'>
                            { !!isOrderSpeedyErrorActive ? <FunctionalError /> : 
                                                           !!isOrderSpeedyWarningActive ? <FunctionalWarning /> : <></>}
                        </div>
                        <div className={`cart-speedy-alerts-title-text ${!!isOrderSpeedyErrorActive ? 'speedy-error' : !!isOrderSpeedyWarningActive ? 'speedy-warning' : ''}`}>
                            { !!isOrderSpeedyErrorActive ? getLabelFallback("SPEEDY_FUNCTIONAL_ERROR_TITLE") : 
                                                           !!isOrderSpeedyWarningActive ? getLabelFallback("SPEEDY_FUNCTIONAL_WARNING_TITLE") : <></>}
                        </div>
                    </div>
                    <div className={`cart-speedy-alerts-body ${!!isOrderSpeedyErrorActive ? 'speedy-error' : !!isOrderSpeedyWarningActive ? 'speedy-warning' : ''}`}>
                        <span dangerouslySetInnerHTML={{__html: !!isOrderSpeedyErrorActive ? getLabelFallback("SPEEDY_FUNCTIONAL_ERROR_BODY") : !!isOrderSpeedyWarningActive ? getLabelFallback("SPEEDY_FUNCTIONAL_WARNING_BODY") : <></> }} />
                    </div>
                </div>
            }

            <div className="cart-EarlyAccessTooltip"> 
            <div className='typ-row'>
                <div className="typ-row-espot" dangerouslySetInnerHTML={{
                    __html: espotEarlyAccessTooltip,}}/>
              	</div>
            </div>
            
            {smartGlassesError !== '' &&
                <TextBanner classes={"isError"}>
                    <div className="cart-error">
                        <span className={`${smartGlassesError == 'TOO_MANY_GLASSES' ? 'bold' : ''}`}>{ getLabelFallback(smartGlassesError + '_ERROR') }</span>
                        <span dangerouslySetInnerHTML={{__html: getLabelFallback(smartGlassesError + '_CLAIM')}}></span>
                    </div>
                </TextBanner>
            }

            {insuranceError && 
                <TextBanner classes={"isError"}>
                    <div className="cart-error">
                        <span>{ getLabelFallback('INSURANCE_ONLY_ONE_PRODUCT_ERROR_LABEL') }</span>
                    </div>
                </TextBanner>
            }
            
            <div className="cart-top-content">
                {isMobile && <Insurance id={'insurance-mobile'}/>}
                <button data-element-id="Summary_Checkout" onClick={()=> {checkoutClick()}} className={`violet-button ${smartGlassesError != '' ? 'disabled' : ''} ${!!isOrderSpeedyErrorActive ? 'disabled' : ''}`}>{ getLabelFallback('CHECKOUT_LABEL') }</button>
            </div>

            {/* SPEEDY RX WARNING MOBILE */}
            {!!isMobile  && (!!isOrderSpeedyErrorActive || !!isOrderSpeedyWarningActive) &&
                <div className={`cart-speedy-alerts mobile ${!!isOrderSpeedyErrorActive ? 'speedy-error' : !!isOrderSpeedyWarningActive ? 'speedy-warning' : ''}`}>
                    <div className='cart-speedy-alerts-title'>
                        <div className='cart-speedy-alerts-title-icon'>
                            { !!isOrderSpeedyErrorActive ? <FunctionalError /> : 
                                                           !!isOrderSpeedyWarningActive ? <FunctionalWarning /> : <></>}
                        </div>
                        <div className={`cart-speedy-alerts-title-text ${!!isOrderSpeedyErrorActive ? 'speedy-error' : !!isOrderSpeedyWarningActive ? 'speedy-warning' : ''}`}>
                            { !!isOrderSpeedyErrorActive ? getLabelFallback("SPEEDY_FUNCTIONAL_ERROR_TITLE") : 
                                                           !!isOrderSpeedyWarningActive ? getLabelFallback("SPEEDY_FUNCTIONAL_WARNING_TITLE") : <></>}
                        </div>
                    </div>
                    <div className={`cart-speedy-alerts-body ${!!isOrderSpeedyErrorActive ? 'speedy-error' : !!isOrderSpeedyWarningActive ? 'speedy-warning' : ''}`}>
                        <span dangerouslySetInnerHTML={{__html: !!isOrderSpeedyErrorActive ? getLabelFallback("SPEEDY_FUNCTIONAL_ERROR_BODY") : !!isOrderSpeedyWarningActive ? getLabelFallback("SPEEDY_FUNCTIONAL_WARNING_BODY") : <></> }} />
                    </div>
                </div>
            }
            
            {
                !cart ? <CardProductTileSkeleton /> : !!cartProductTile ? cartProductTile : <CardProductTileSkeleton />
            }
            
            {config?.mentionMeEnabled && <div id="mmWrapper"></div>}
            {!!cart && !!formattedOrderItems ?
                <div className="cart-accordion-container">
                    <div className="cart-accordion-middleware">
                        <div className="cart-accordion">
                            {visiblePromo === false ?
                                <button 
                                    data-element-id='PromoCode_Open'
                                    aria-label={ getLabelFallback('PROMO_TITLE')}
                                    className="cart-accordion-heading" 
                                    onClick={() => {setPromoVisible(visiblePromo => visiblePromo = !visiblePromo )}}>
                                    <h2>{ getLabelFallback('PROMO_TITLE')}</h2>
                                    <span><SimplePlusIcon /></span>
                                </button>
                                :
                                <FormProvider {...methods}>
                                    <form className="cart-accordion-form"
                                        onSubmit={methods.handleSubmit(submitPromoCode)} onChange={onChangeHandler} onReset={onResetHandler}
                                    >
                                        <div className="cart-accordion-input">
                                            <input
                                                {...methods.register("promo-code", {required: true})}
                                                className="cart-accordion-form-field input-element"
                                                id="promo-code"
                                                type="text"
                                                placeholder={getLabelFallback('PROMO_LABEL')}
                                            />
                                            <button type="reset" aria-label={getLabelFallback('RESET_PROMO_CODE_LABEL')} className="cart-accordion-form-reset"><Close width={"14px"} height={"14px"} /></button>
                                        </div>
                                        <button data-element-id="PromoCode_Submit" type="submit" className="violet-button">{getLabelFallback('PROMO_BUTTON')}</button>
                                    </form>
                                </FormProvider>
                            }
                        </div>
                        <div className="cart-accordion-form-feedback">
                            {methods.formState.errors['promo-code'] ? (
                                <span className="error">
                                    {methods.formState.errors['promo-code'].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', getLabelFallback('PROMO_FIELD_NAME'))}
                                </span>
                            ) :
                            showPromoCodeFeedback === true && promoCodeFeedback
                            }
                        </div>                    
                    </div>

                    <div className="cart-accordion-middleware">
                        <div className="cart-accordion">
                            <button 
                                data-element-id='IDme_Open'
                                aria-label={ getLabelFallback('SPECIAL_DISCOUNT_TITLE')}
                                className="cart-accordion-heading cart-accordion-heading__mod" 
                                onClick={() => {setVerifyVisible(visibleVerify => visibleVerify = !visibleVerify )}}>
                                
                                <div style={{display: !visibleVerify ? 'flex' : 'none'}}>
                                    <h2>{ getLabelFallback('SPECIAL_DISCOUNT_TITLE') }</h2>
                                    <span><SimplePlusIcon /></span>
                                </div>
                                    
                                <div style={{display: !visibleVerify ? 'none' : 'flex', visibility: !visibleVerify ? 'hidden' : 'visible', width: !visibleVerify ? '0px' : '100%', height: '100%', position: 'relative'}}>
                                    <span id="idme-wallet-button"
                                        data-scope={config.IDMe.dataScope}
                                        data-client-id={config.IDMe.dataClientId}
                                        data-redirect={config.IDMe.dataRedirect}
                                        data-response="code"
                                        data-show-verify="true">
                                    </span>
                                    <span style={{position: 'absolute', right: '0', top: '50%', transform: 'translateY(-50%)'}}><SimpleMinusIcon /></span>
                                </div>
                            </button>
                        </div>
                        {!!cart && !!cart.promotionCode && cart.promotionCode.filter(p => p.code.search('IDME') !== -1).length > 0 &&
                            <div className={`cart-accordion-form-feedback ${visibleVerify ? 'lower' : ''}`}>
                                <span className="success">
                                    {getLabelFallback('IDME_FEEDBACK')}
                                </span>
                            </div>
                        }
                    </div>
                </div>:
                <div style={{marginTop: '25px'}}>
                    <SimpleElementSkeleton width={'100%'} height={'55px'}/>
                </div>
            }
            {children}
        </section>
    );
}
export default Cart;
